import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ProductCard from '../components/ProductCard';
import Lightbox from '../components/Lightbox';
import Reveal from '../components/ui/Reveal';
import NotFound from './NotFound';
import { categories, countIn, getCategory, productsIn } from '../data/collections';
import { useEnquiry } from '../context/EnquiryContext';
import usePageMeta from '../hooks/usePageMeta';
import SectionHeading from '../components/ui/SectionHeading';

export default function CollectionDetail() {
  const { slug } = useParams();
  const category = getCategory(slug);
  const items = category ? productsIn(slug) : [];

  const { openEnquiry, resetViews } = useEnquiry();
  const [lightbox, setLightbox] = useState(null);

  // Fresh photo count each time a different collection is opened.
  useEffect(() => {
    resetViews();
  }, [slug, resetViews]);

  usePageMeta({
    title: category ? `${category.name} | Pramukh Jewellers, Ahmedabad` : 'Collection not found',
    description: category?.blurb,
    path: `/collections/${slug}`,
    image: category?.cover,
  });

  if (!category) return <NotFound />;

  const others = categories.filter((c) => c.slug !== slug);

  return (
    <>
      <PageHeader
        eyebrow={category.tagline}
        title={category.name}
        native={category.native}
        lede={category.blurb}
        image={category.cover}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Collections', to: '/collections' }, { label: category.name }]}
      />

      {/* Grid */}
      <section className="bg-cream py-14 lg:py-20">
        <div className="shell">
          <div className="mb-14 flex flex-wrap items-center justify-between gap-4 border-b border-royal/10 pb-5">
            <p className="text-[11.5px] uppercase tracking-wider2 text-royal/80">
              {String(items.length).padStart(2, '0')} {items.length === 1 ? 'piece' : 'pieces'}
            </p>
            <button type="button" onClick={() => openEnquiry(category.name, 'manual')} className="btn-line">
              <span className="btn-line-rule" />
              Enquire
            </button>
          </div>

          {items.length === 0 ? (
            <p className="py-16 text-center font-display text-2xl text-royal/80">
              New pieces are being photographed for this collection.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-x-5 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 70}>
                  <ProductCard
                    product={p}
                    index={i}
                    priority={i < 3}
                    contextLabel={category.name}
                    onOpen={setLightbox}
                  />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Enquiry band */}
      <section className="relative overflow-hidden bg-royal py-20 lg:py-24">
        <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.12]" />
        <div className="shell relative flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            tone="light"
            eyebrow="Enquire"
            title="Seen something you like?"
            lede="Send us the piece and we will come back with weight, purity and today’s price — and tell you what else has just come in."
          />
          <Reveal delay={120} className="flex shrink-0 flex-wrap items-center gap-x-10 gap-y-4">
            <button type="button" onClick={() => openEnquiry(category.name, 'manual')} className="btn-line-light">
              <span className="btn-line-rule" />
              Enquire on WhatsApp
            </button>
            <Link
              to="/contact"
              className="text-[12px] font-medium uppercase tracking-luxe text-cream/70 transition-colors duration-500 hover:text-gold-200"
            >
              Visit the showroom
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Keep browsing */}
      <section className="border-t border-royal/10 bg-ivory py-20 lg:py-24">
        <div className="shell">
          <div className="mb-12 flex items-end justify-between gap-6 border-b border-royal/10 pb-5">
            <h2 className="font-display text-[1.7rem] leading-tight text-royal sm:text-[2.1rem]">
              Keep browsing
            </h2>
            <Link to="/collections" className="btn-line shrink-0">
              <span className="btn-line-rule" />
              All collections
            </Link>
          </div>

          <div className="no-scrollbar flex gap-5 overflow-x-auto pb-2 lg:grid lg:grid-cols-5 lg:overflow-visible">
            {others.slice(0, 5).map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                className="group block w-[56vw] shrink-0 sm:w-[220px] lg:w-auto"
              >
                <div className="wipe relative aspect-[4/5] overflow-hidden bg-beige/60">
                  <img
                    src={c.cover}
                    alt={c.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.06]"
                  />
                </div>
                <div className="mt-3.5 border-t border-royal/10 pt-3">
                  <h3 className="font-display text-[1.05rem] leading-tight text-royal transition-colors duration-500 group-hover:text-gold-600">
                    {c.name}
                  </h3>
                  <p className="mt-1 text-[11.5px] uppercase tracking-wider2 text-royal/75">
                    {countIn(c.slug)} pieces
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lightbox !== null && (
        <Lightbox
          items={items}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
          contextLabel={category.name}
        />
      )}
    </>
  );
}
